import noop from 'lodash/noop';
import React from 'react';
import styled from 'styled-components';
import {
    IM_PAYING_MYSELF_CAPITALIZED_TEXT,
    InsuranceForm,
    IM_PAYING_MYSELF,
    HAS_SET_SELF_PAY,
} from '@zocdoc-frontend-common/components-insuranceform';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { getSessionStorageItem } from '@zocdoc-frontend-common/utils-storage';
import { isVaccineProcedure } from '../../utils/isCovidVaccine';

/**
 * @param {boolean} hasUnsetOrShouldUnsetInsuranceOnLoad
 * @param {string} insuranceCarrierId
 * @param {boolean} insuranceIsAccepted
 * @param {boolean} insuranceIsValidating
 * @param {string} insurancePlanId
 * @param {function(carrierId: string): void} onChangeCarrier
 * @param {function(planId: string): void} onChangePlan
 * @param {function(): void} onClickCarrier
 * @param {function(): void} onClickPlan
 * @param {string} procedureId
 * @param {boolean} receivedBookingState
 * @param {string} specialtyId
 * @param {function(carrierId: string): void} trackPreloadInsuranceCarrier
 * @param {function(planId: string): void} trackPreloadInsurancePlan
 * @returns {JSX.Element}
 * @constructor
 */
export class InsuranceFormWrapper extends React.Component {
    static defaultProps = {
        insuranceCarrierId: '-2',
        insurancePlanId: '-1',
        onChangeCarrier: noop,
        onChangePlan: noop,
        onClickCarrier: noop,
        onClickPlan: noop,
        trackPreloadInsuranceCarrier: noop,
        trackPreloadInsurancePlan: noop,
        receivedBookingState: false,
        specialtyId: undefined,
        hasUnsetOrShouldUnsetInsuranceOnLoad: false,
        shouldShowInsuranceScanner: false,
    };

    _trackedPreloadIds = false;

    componentDidMount() {
        const {
            insuranceCarrierId,
            onChangeCarrier,
            hasUnsetOrShouldUnsetInsuranceOnLoad,
        } = this.props;

        if (
            hasUnsetOrShouldUnsetInsuranceOnLoad &&
            insuranceCarrierId != null
        ) {
            onChangeCarrier(null);
        }
    }

    componentDidUpdate() {
        const {
            insuranceCarrierId,
            insurancePlanId,
            receivedBookingState,
            trackPreloadInsuranceCarrier,
            trackPreloadInsurancePlan,
        } = this.props;

        if (receivedBookingState && !this._trackedPreloadIds) {
            // Received booking state for the first time
            trackPreloadInsuranceCarrier(insuranceCarrierId);
            trackPreloadInsurancePlan(insurancePlanId);
            this._trackedPreloadIds = true;
        }
    }

    render() {
        const {
            insuranceCarrierId,
            insurancePlanId,
            insuranceIsAccepted,
            onChangeCarrier,
            onChangePlan,
            onClickCarrier,
            onClickPlan,
            procedureId,
            providerFullName,
            specialtyId,
            shouldShowMentalHealthOONBenefits,
            insuranceIsValidating,
            hasUnsetOrShouldUnsetInsuranceOnLoad,
            insuranceType,
        } = this.props;

        const isInsuranceOutOfNetwork =
            parseInt(insuranceCarrierId, 10) >= 0 && !insuranceIsAccepted;
        const imPayingMyselfText = isVaccineProcedure(procedureId)
            ? "I Don't Have Insurance"
            : IM_PAYING_MYSELF_CAPITALIZED_TEXT;

        return (
            <>
                <InsuranceForm
                    carrierId={insuranceCarrierId}
                    imPayingMyselfText={imPayingMyselfText}
                    insuranceType={insuranceType}
                    onChangeCarrier={onChangeCarrier}
                    onClickCarrier={onClickCarrier}
                    onClickPlan={onClickPlan}
                    onChangePlan={onChangePlan}
                    planId={insurancePlanId}
                    specialtyId={specialtyId}
                    showCarrierPlaceholder={
                        hasUnsetOrShouldUnsetInsuranceOnLoad
                    }
                />
                {shouldShowMentalHealthOONBenefits &&
                    !insuranceIsValidating &&
                    isInsuranceOutOfNetwork && (
                        <OONParagraph data-test="out-of-network-status">
                            Out-of-network for {providerFullName}
                        </OONParagraph>
                    )}
            </>
        );
    }
}

export const getHasUnsetOrShouldUnsetInsuranceOnLoad = ({
    insuranceCarrierId,
}) => {
    // if session storage HAS_SET_SELF_PAY is true, meaning user explicitly chose I'm paying myself
    // then don't show place holder text
    // otherwise, it means backend/monolith set carrier id to be I'm paying myself
    // in that case we want to unset carrier id and show placeholder if carrier id is paying for myself
    // or just show placeholder if carrier id is null which means it has been unset already
    // if carrier id is null, refresh the page will cause carrier id be set to '0' in backend
    return (
        (insuranceCarrierId === IM_PAYING_MYSELF ||
            isInsuranceUnset(insuranceCarrierId)) &&
        getSessionStorageItem(HAS_SET_SELF_PAY) !== 'true'
    );
};

const isInsuranceUnset = (carrierId) => {
    return carrierId === null || carrierId === '0';
};

const OONParagraph = styled.p`
    color: #0060cc;
    font: 13px/20px ${FontFamily.SharpSansSemibold};
    margin-top: 4px;
`;
